import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  CreditCard, Key, Eye, EyeOff, CheckCircle, XCircle, 
  Loader2, ExternalLink, Shield, Info, Zap
} from 'lucide-react';
import { toast } from 'sonner';

export default function PaymentSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  
  const [settings, setSettings] = useState({
    razorpay_enabled: false,
    razorpay_key_id: '',
    razorpay_key_secret: '',
    razorpay_key_secret_masked: '',
    global_fallback_available: false
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await api.get('/payment/settings');
      setSettings({
        razorpay_enabled: res.data.razorpay_enabled || false,
        razorpay_key_id: res.data.razorpay_key_id || '',
        razorpay_key_secret: '',
        razorpay_key_secret_masked: res.data.razorpay_key_secret_masked || '',
        global_fallback_available: res.data.global_fallback_available || false
      });
    } catch (error) {
      toast.error('Failed to load payment settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/payment/settings', {
        razorpay_enabled: settings.razorpay_enabled,
        razorpay_key_id: settings.razorpay_key_id || null,
        razorpay_key_secret: settings.razorpay_key_secret || null
      });
      toast.success('Payment settings saved successfully');
      
      // Clear the secret field and reload
      setSettings(prev => ({ ...prev, razorpay_key_secret: '' }));
      loadSettings();
      setConnectionStatus(null);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setConnectionStatus(null);
    try {
      const res = await api.post('/payment/test-connection');
      setConnectionStatus(res.data);
    } catch (error) {
      setConnectionStatus({ success: false, message: 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="payment-settings-loading">
        <div className="text-center">
          <div className="h-12 w-12 rounded-full border-4 border-primary border-t-transparent animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading payment settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="payment-settings">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Payment Gateway</h1>
        <p className="text-gray-500">Configure Razorpay for accepting online payments</p>
      </div>

      {/* Global Fallback Notice */}
      {settings.global_fallback_available && !settings.razorpay_key_id && (
        <Alert className="bg-blue-50 border-blue-200">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-700">
            A global Razorpay configuration is available. Your hotel will use this unless you configure your own credentials below.
          </AlertDescription>
        </Alert>
      )}

      {/* Razorpay Configuration */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <CardTitle>Razorpay</CardTitle>
                <CardDescription>Accept cards, UPI, netbanking & more</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="razorpay-enabled" className="text-sm">Enable</Label>
              <Switch
                id="razorpay-enabled"
                checked={settings.razorpay_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, razorpay_enabled: checked })}
                data-testid="razorpay-enabled-switch"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* API Keys */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="key-id">API Key ID</Label>
              <div className="relative mt-1">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="key-id"
                  value={settings.razorpay_key_id}
                  onChange={(e) => setSettings({ ...settings, razorpay_key_id: e.target.value })}
                  placeholder="rzp_live_xxxxxxxxxx or rzp_test_xxxxxxxxxx"
                  className="pl-10"
                  data-testid="razorpay-key-id"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Found in Razorpay Dashboard → Settings → API Keys
              </p>
            </div>

            <div>
              <Label htmlFor="key-secret">API Key Secret</Label>
              <div className="relative mt-1">
                <Shield className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="key-secret"
                  type={showSecret ? 'text' : 'password'}
                  value={settings.razorpay_key_secret}
                  onChange={(e) => setSettings({ ...settings, razorpay_key_secret: e.target.value })}
                  placeholder={settings.razorpay_key_secret_masked || "Enter secret key"}
                  className="pl-10 pr-10"
                  data-testid="razorpay-key-secret"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {settings.razorpay_key_secret_masked && !settings.razorpay_key_secret && (
                <p className="text-xs text-green-600 mt-1">
                  Secret key is configured ({settings.razorpay_key_secret_masked})
                </p>
              )}
            </div>
          </div>

          {/* Connection Status */}
          {connectionStatus && (
            <Alert className={connectionStatus.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
              {connectionStatus.success ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <XCircle className="h-4 w-4 text-red-600" />
              )}
              <AlertDescription className={connectionStatus.success ? 'text-green-700' : 'text-red-700'}>
                {connectionStatus.message}
                {connectionStatus.using_global && (
                  <span className="block text-xs mt-1">(Using global configuration)</span>
                )}
                {connectionStatus.key_id_preview && (
                  <span className="block text-xs mt-1">Key: {connectionStatus.key_id_preview}</span>
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={handleTestConnection}
              disabled={testing}
              data-testid="test-connection-btn"
            >
              {testing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Zap className="h-4 w-4 mr-2" />
              )}
              Test Connection
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-primary hover:bg-primary/90"
              data-testid="save-settings-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Save Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card className="bg-gray-50 border-gray-200">
        <CardHeader>
          <CardTitle className="text-base">How to get Razorpay API Keys?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-gray-600">
          <ol className="list-decimal list-inside space-y-2">
            <li>Log in to your <a href="https://dashboard.razorpay.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">Razorpay Dashboard <ExternalLink className="h-3 w-3" /></a></li>
            <li>Go to <strong>Settings → API Keys</strong></li>
            <li>Click <strong>Generate Key</strong> to create new keys</li>
            <li>Copy the <strong>Key ID</strong> and <strong>Key Secret</strong></li>
            <li>Paste them in the fields above</li>
          </ol>
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800 text-xs">
              <strong>Test Mode:</strong> Use keys starting with <code className="bg-yellow-100 px-1 rounded">rzp_test_</code> for testing. 
              Switch to <code className="bg-yellow-100 px-1 rounded">rzp_live_</code> keys for production.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Payment History Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent Payments</CardTitle>
          <CardDescription>Last 5 payment transactions</CardDescription>
        </CardHeader>
        <CardContent>
          <PaymentHistory />
        </CardContent>
      </Card>
    </div>
  );
}

// Payment History Component
function PaymentHistory() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPayments();
  }, []);

  const loadPayments = async () => {
    try {
      const res = await api.get('/payments');
      setPayments(res.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-4 text-gray-500">Loading...</div>;
  }

  if (payments.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <CreditCard className="h-8 w-8 mx-auto mb-2 text-gray-300" />
        <p>No payments yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {payments.map((payment) => (
        <div key={payment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div>
            <p className="font-medium">₹{payment.amount?.toLocaleString()}</p>
            <p className="text-xs text-gray-500">
              {new Date(payment.created_at).toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
          <Badge 
            variant={payment.status === 'paid' ? 'default' : payment.status === 'created' ? 'secondary' : 'destructive'}
            className={payment.status === 'paid' ? 'bg-green-100 text-green-700' : ''}
          >
            {payment.status}
          </Badge>
        </div>
      ))}
    </div>
  );
}
